/**
 * ImportTimestampsDialogV2 — V2 restyle of the v1
 * ImportTimestampsDialog (TappingPointsTimestamp/ImportTimestampsDialog.js).
 *
 * Same parse / validate / shape-the-result logic — the only difference
 * is chrome: the v1 component is a stack of MUI primitives (Dialog,
 * TextField, Alert) and the V2 version uses the Modal primitive +
 * Textarea + brand-tinted result panels.
 *
 *   <ImportTimestampsDialogV2
 *     open={importOpen}
 *     onClose={() => setImportOpen(false)}
 *     tappingPoints={catalog}
 *     onImport={(rows) => …}
 *     maxDurationSeconds={formData.session_length_in_sec}
 *   />
 *
 * Result shape (per row passed to onImport) matches v1 exactly so the
 * parent's `onImportTimestamps` consumer can stay unchanged:
 *   {
 *     timestamp:        number   // total seconds
 *     tapping_point_id: number
 *     image_url:        string
 *     originalLine:     number   // 1-based source-line index
 *     displayTime:      string   // "MM:SS" for the result preview
 *     pointName:        string   // resolved canonical catalog name
 *   }
 */
import React, { useMemo, useState } from 'react';
import Modal from '../../redesign/Modal';
import { Button } from '../../redesign';
import { Textarea } from '../../redesign/edit/TextInput';

/* ─── Helpers (mirrors v1 ImportTimestampsDialog) ──────────────────── */

function parseTimestamp(minutesStr, secondsStr, maxDurationSeconds) {
  const minutes = parseFloat(minutesStr?.trim() || '0');
  const seconds = parseFloat(secondsStr?.trim() || '0');

  if (Number.isNaN(minutes) || Number.isNaN(seconds)) {
    return { error: 'Invalid number format' };
  }
  if (minutes < 0 || seconds < 0) {
    return { error: 'Minutes and seconds cannot be negative' };
  }
  if (seconds >= 60) {
    return { error: 'Seconds must be less than 60' };
  }
  if (minutes > 60) {
    return { error: 'Minutes cannot be greater than 60' };
  }

  const totalSeconds = Math.floor(minutes * 60 + seconds);
  if (maxDurationSeconds && totalSeconds > maxDurationSeconds) {
    return {
      error: `Timestamp ${minutes}:${String(Math.floor(seconds)).padStart(2, '0')} exceeds audio length`,
    };
  }
  return { totalSeconds };
}

function findTappingPoint(pointName, tappingPoints) {
  if (!pointName?.trim()) {
    return { error: 'Tapping point name is required' };
  }
  const clean = pointName.trim().toLowerCase();
  const hit = (tappingPoints || []).find(
    (tp) => tp.tapping_point_name?.toLowerCase() === clean,
  );
  if (!hit) {
    return { error: `Tapping point "${pointName}" not found in allowed list` };
  }
  return { tappingPoint: hit };
}

function parseSpreadsheetData(data, tappingPoints, maxDurationSeconds) {
  const lines = data.split('\n').filter((l) => l.trim());
  const results = [];
  const parseErrors = [];
  const parseWarnings = [];

  lines.forEach((line, index) => {
    const lineNumber = index + 1;
    const cols = line.split(/\t|,/).map((c) => c.trim());

    if (cols.length < 3) {
      parseErrors.push(
        `Line ${lineNumber}: Expected at least 3 columns (minutes, seconds, tapping point)`,
      );
      return;
    }

    const [minutesStr, secondsStr, pointName, ...extra] = cols;
    const t = parseTimestamp(minutesStr, secondsStr, maxDurationSeconds);
    if (t.error) {
      parseErrors.push(`Line ${lineNumber}: ${t.error}`);
      return;
    }
    const p = findTappingPoint(pointName, tappingPoints);
    if (p.error) {
      parseErrors.push(`Line ${lineNumber}: ${p.error}`);
      return;
    }
    if (extra.length > 0 && extra.some((c) => c.trim())) {
      parseWarnings.push(`Line ${lineNumber}: Extra data ignored: ${extra.join(', ')}`);
    }

    results.push({
      timestamp: t.totalSeconds,
      tapping_point_id: p.tappingPoint.tapping_point_id,
      image_url: p.tappingPoint.tapping_point_image_url,
      originalLine: lineNumber,
      displayTime: `${Math.floor(t.totalSeconds / 60)}:${String(t.totalSeconds % 60).padStart(2, '0')}`,
      pointName: p.tappingPoint.tapping_point_name,
    });
  });

  return { results, errors: parseErrors, warnings: parseWarnings };
}

/* ─── Color cycle for the catalog chip list (same one
 *  TappingPointsEditor uses so the names read consistently) ────── */
const BADGE_TINTS = [
  { bg: '#75A293', fg: '#fff' },
  { bg: '#E58A6A', fg: '#fff' },
  { bg: '#C4715B', fg: '#fff' },
  { bg: '#2C3E50', fg: '#fff' },
  { bg: '#A0B589', fg: '#fff' },
];
function badgeTint(id) {
  if (id == null) return BADGE_TINTS[0];
  const n = Math.abs(Number(id)) || 0;
  return BADGE_TINTS[n % BADGE_TINTS.length];
}
function tpInitials(name) {
  if (!name) return '··';
  const words = String(name).trim().split(/\s+/);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return words[0].slice(0, 2).toUpperCase();
}

/* ─── Main ─────────────────────────────────────────────────────── */

export default function ImportTimestampsDialogV2({
  open,
  onClose,
  onImport,
  tappingPoints,
  maxDurationSeconds = null,
}) {
  const [pastedData, setPastedData] = useState('');
  const [errors, setErrors] = useState([]);
  const [warnings, setWarnings] = useState([]);

  const handleClose = () => {
    setPastedData('');
    setErrors([]);
    setWarnings([]);
    onClose?.();
  };

  const handleImport = () => {
    if (!pastedData.trim()) {
      setErrors(['Please paste spreadsheet data before importing']);
      setWarnings([]);
      return;
    }
    const { results, errors: parseErrors, warnings: parseWarnings } =
      parseSpreadsheetData(pastedData, tappingPoints, maxDurationSeconds);
    setErrors(parseErrors);
    setWarnings(parseWarnings);
    if (parseErrors.length === 0 && results.length > 0) {
      onImport?.(results);
      handleClose();
    }
  };

  const availablePoints = useMemo(
    () =>
      (tappingPoints || []).filter(
        (tp) =>
          tp.tapping_point_name &&
          tp.tapping_point_name !== 'Default Tapping Point',
      ),
    [tappingPoints],
  );

  return (
    <Modal
      open={open}
      onClose={handleClose}
      eyebrow="Tapping points"
      title="Import from spreadsheet"
      maxWidth={640}
      footer={
        <>
          <Button variant="ghost" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="coral"
            onClick={handleImport}
            disabled={!pastedData.trim()}
          >
            Import timestamps
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* Intro + format hint */}
        <div>
          <p
            className="text-text-muted"
            style={{ fontSize: 12.5, margin: 0, lineHeight: 1.5 }}
          >
            Paste one row per timestamp, three columns each: minutes,
            seconds, tapping-point name. Tabs or commas work — copy
            directly from Excel or Google Sheets.
          </p>
          <div
            className="font-mono text-navy"
            style={{
              marginTop: 8,
              padding: '8px 10px',
              background: '#FAF4EA',
              border: '1px solid #E8E0CF',
              borderRadius: 6,
              fontSize: 11.5,
              whiteSpace: 'pre',
              overflowX: 'auto',
            }}
          >
            {`Minutes\tSeconds\tTapping Point\n1\t30\tKarate Chop\n2\t15\tEyebrow`}
          </div>
        </div>

        {/* Paste area */}
        <Textarea
          rows={8}
          value={pastedData}
          onChange={(e) => setPastedData(e.target.value)}
          placeholder={'Paste your rows here…'}
          aria-label="Spreadsheet data"
        />

        {/* Available catalog — chips with the same colored badge cycle
            the row list uses, so names match what shows up in the
            timeline after import. */}
        <div>
          <div
            className="font-condensed font-bold uppercase text-text-light"
            style={{
              fontSize: 11,
              letterSpacing: '0.08em',
              marginBottom: 6,
              display: 'flex',
              alignItems: 'baseline',
              gap: 6,
            }}
          >
            <span>Available tapping points</span>
            <span
              className="font-mono"
              style={{
                textTransform: 'none',
                letterSpacing: 0,
                fontWeight: 500,
                color: '#9CA1A8',
                fontSize: 10.5,
              }}
            >
              · {availablePoints.length}
            </span>
          </div>
          <div
            className="flex flex-wrap"
            style={{
              gap: 6,
              padding: '10px 12px',
              background: '#FAF4EA',
              border: '1px solid #E8E0CF',
              borderRadius: 6,
              maxHeight: 130,
              overflowY: 'auto',
            }}
          >
            {availablePoints.length === 0 ? (
              <span className="text-text-muted" style={{ fontSize: 12 }}>
                No tapping points configured.
              </span>
            ) : (
              availablePoints.map((tp) => {
                const tint = badgeTint(tp.tapping_point_id);
                return (
                  <span
                    key={tp.tapping_point_id}
                    className="inline-flex items-center"
                    style={{
                      gap: 6,
                      padding: '2px 8px 2px 2px',
                      borderRadius: 999,
                      background: '#FAF7F1',
                      border: '1px solid #E8E0CF',
                      fontSize: 12,
                      color: '#2C3E50',
                    }}
                  >
                    <span
                      className="font-condensed font-bold grid place-items-center"
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        background: tint.bg,
                        color: tint.fg,
                        fontSize: 9.5,
                        letterSpacing: '0.02em',
                      }}
                    >
                      {tpInitials(tp.tapping_point_name)}
                    </span>
                    {tp.tapping_point_name}
                  </span>
                );
              })
            )}
          </div>
        </div>

        {/* Warnings */}
        {warnings.length > 0 && (
          <div
            style={{
              padding: '10px 12px',
              background: '#FCEAD1',
              border: '1px solid #E8C796',
              borderRadius: 6,
            }}
          >
            <div
              className="font-condensed font-bold uppercase"
              style={{
                color: '#8C5A1E',
                fontSize: 10.5,
                letterSpacing: '0.08em',
                marginBottom: 4,
              }}
            >
              Warnings
            </div>
            <ul
              style={{
                margin: 0,
                paddingLeft: 18,
                fontSize: 12.5,
                color: '#8C5A1E',
                lineHeight: 1.55,
              }}
            >
              {warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Errors */}
        {errors.length > 0 && (
          <div
            style={{
              padding: '10px 12px',
              background: '#E58A6A1F',
              border: '1px solid #E58A6A',
              borderRadius: 6,
            }}
          >
            <div
              className="font-condensed font-bold uppercase"
              style={{
                color: '#C4715B',
                fontSize: 10.5,
                letterSpacing: '0.08em',
                marginBottom: 4,
              }}
            >
              Import errors
            </div>
            <ul
              style={{
                margin: 0,
                paddingLeft: 18,
                fontSize: 12.5,
                color: '#C4715B',
                lineHeight: 1.55,
              }}
            >
              {errors.map((e, i) => (
                <li key={i}>{e}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Modal>
  );
}
